import { useCallback, useEffect, useState } from "react";
import { Lock, ShieldCheck, Download, Trash2, HardDrive, Server, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Section, Eyebrow, H1, Lead, FadeIn } from "../components/Primitives";
import {
  storageStats,
  exportAll,
  wipeEverything,
  isLocalEncryptedAvailable,
} from "../lib/localdb";

function fmtBytes(n) {
  if (!n) return "0 KB";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function MyDataPage() {
  const supported = isLocalEncryptedAvailable();
  const [stats, setStats] = useState({ count: 0, bytes: 0 });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await storageStats();
      setStats(s);
    } catch (e) {
      console.warn("Could not read storage stats:", e);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onExport = async () => {
    setBusy(true);
    try {
      const data = await exportAll();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `register-to-digital-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.items.length} items`);
    } catch (e) {
      console.error("Export failed:", e);
      toast.error("Export failed. Open the browser console for details.");
    } finally {
      setBusy(false);
    }
  };

  const onWipe = async () => {
    const ok = window.confirm(
      "Permanently wipe ALL local data on this device?\n\n" +
        "This deletes every saved scan, error-check and search from this browser. " +
        "It cannot be undone.",
    );
    if (!ok) return;
    setBusy(true);
    try {
      await wipeEverything();
      await refresh();
      toast.success("All local data wiped. A fresh encryption key has been generated.");
    } catch (e) {
      console.error("Wipe failed:", e);
      toast.error("Wipe failed. Open the browser console for details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-my-data">
      <Section className="pt-24 pb-10">
        <FadeIn><Eyebrow>My Data</Eyebrow></FadeIn>
        <FadeIn delay={0.05}>
          <H1 className="mt-6 max-w-3xl">Your data lives on your device.</H1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <Lead className="mt-6 max-w-2xl">
            Every scan, error-check and search you do is saved <strong>only on this device</strong>, encrypted at rest.
            Nothing is uploaded to our servers and nothing follows you across devices.
          </Lead>
        </FadeIn>
      </Section>

      <Section className="pt-0">
        <div
          data-testid="my-data-status"
          className="overflow-hidden rounded-3xl border border-emerald-200 bg-emerald-50"
        >
          <div className="grid gap-6 p-6 sm:p-8 md:grid-cols-3">
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-900 ring-1 ring-inset ring-emerald-300">
                <Lock className="h-3.5 w-3.5" /> Stored locally · AES-GCM encrypted
              </div>
              <h2 className="mt-4 font-poppins text-2xl font-semibold text-emerald-950">
                Zero-server data architecture
              </h2>
              <p className="mt-2 max-w-xl text-sm text-emerald-900/80">
                We never store your scans, history or text content. The OCR engine runs as a stateless
                gateway: your photo is read once, returned to your browser, and immediately discarded.
              </p>
            </div>
            <div className="grid place-items-center">
              <div className="rounded-full border border-emerald-300 bg-white p-6">
                <ShieldCheck className="h-12 w-12 text-emerald-700" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-zinc-700" />
              <h3 className="font-poppins text-sm font-semibold text-zinc-950">On this device</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-zinc-700">
              <li>• All scan results &amp; error-check history</li>
              <li>• Search log (titles + timestamps)</li>
              <li>• AES-GCM-256 sealed in IndexedDB</li>
              <li>• Encryption key non-extractable from this browser</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-zinc-700" />
              <h3 className="font-poppins text-sm font-semibold text-zinc-950">On our servers</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-zinc-700">
              <li>• <span className="font-medium">Nothing persistent</span> from your scans</li>
              <li>• Photo bytes: processed once, then discarded</li>
              <li>• Anonymous counters (e.g. &quot;1 scan&quot;) — no content</li>
              <li>• Your Google account (so you can sign in)</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">Local storage on this device</div>
            <div className="mt-3 flex items-baseline gap-3">
              <div data-testid="my-data-count" className="font-poppins text-3xl font-semibold text-zinc-950">{stats.count}</div>
              <div className="text-sm text-zinc-600">item{stats.count === 1 ? "" : "s"}</div>
            </div>
            <p className="mt-1 text-xs text-zinc-500" data-testid="my-data-bytes">≈ {fmtBytes(stats.bytes)} on disk</p>
            {!supported ? (
              <p className="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-900 ring-1 ring-inset ring-amber-200">
                This browser can&apos;t use local encryption. Try a modern browser (Chrome, Safari, Firefox).
              </p>
            ) : null}
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">Your data, your control</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="my-data-export"
                disabled={busy || stats.count === 0}
                onClick={onExport}
                variant="outline"
                className="rounded-full border-zinc-300"
              >
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                Export as JSON
              </Button>
              <Button
                data-testid="my-data-wipe"
                disabled={busy || stats.count === 0}
                onClick={onWipe}
                variant="outline"
                className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="mr-1.5 h-4 w-4" /> Wipe everything
              </Button>
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              Export gives you a single decrypted JSON file. Wipe permanently deletes every item AND rotates the encryption key.
            </p>
          </div>
        </div>

        <details className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5 text-sm text-zinc-700">
          <summary className="cursor-pointer font-poppins text-sm font-semibold text-zinc-950">
            <FileText className="mr-1.5 inline h-4 w-4" /> Technical details
          </summary>
          <ul className="mt-3 space-y-1.5 text-xs leading-relaxed">
            <li>· <strong>Storage:</strong> Dexie.js over IndexedDB (named <code>RegisterToDigital</code>)</li>
            <li>· <strong>Cipher:</strong> AES-GCM 256-bit, 96-bit random IV per record</li>
            <li>· <strong>Key:</strong> generated by <code>crypto.subtle.generateKey</code> with <code>extractable=false</code> — cannot be exported even by JavaScript on this origin</li>
            <li>· <strong>Key store:</strong> isolated IndexedDB (<code>__rtd_secret__</code>) separate from the data DB</li>
            <li>· <strong>Plaintext fields:</strong> only the item title, kind, and timestamp (for fast sidebar listing without decryption)</li>
            <li>· <strong>Encrypted fields:</strong> all rows, text, columns, suggestions, OCR notes</li>
            <li>· <strong>Wipe:</strong> clears history, deletes the key store, then a fresh key is generated on next save</li>
          </ul>
        </details>
      </Section>
    </div>
  );
}
